import { apiClient } from '@/api/api'
import type { TasteConfig, TasteResults } from '../types/taste-config'
import { mapStyleImageFromBackend } from '@/utils/style-image-mapper'

export interface TasteSubmitRequest {
  tasteConfigId: string
  answers: Record<string, any>
  metadata?: {
    completedAt?: string
    deviceType?: string
    language?: string
  }
  userProfileId?: number | null
}

export interface FetchTasteConfigParams {
  configId?: string
  // resume параметр удален, так как его нет в документации бекендера
}

interface TasteConfigResponse {
  config: TasteConfig
}

export async function fetchTasteConfig(params?: FetchTasteConfigParams): Promise<TasteConfig> {
  try {
    const queryParams = new URLSearchParams()
    if (params?.configId) {
      // Используем snake_case для query параметров
      queryParams.append('config_id', params.configId)
    }

    const url = queryParams.toString()
      ? `/taste/config?${queryParams.toString()}`
      : '/taste/config'

    const response = await apiClient.get<TasteConfigResponse>(url)
    // Бекенд возвращает объект с полем config, извлекаем его
    const config = response.data.config

    // Преобразуем шаг style_evaluation с id "style-evaluation-choice" в формат choice
    if (config.steps) {
      config.steps = config.steps.map((step: any) => {
        // Если это шаг style_evaluation с id "style-evaluation-choice", преобразуем его в choice
        if (step.type === 'style_evaluation' && step.id === 'style-evaluation-choice') {
          // Поддерживаем оба формата: camelCase (после трансформации) и snake_case (до трансформации)
          const selectedStyles = step.selectedStyles || step.selected_styles || []
          const swipesForAccuracy = step.swipesForAccuracy || step.swipes_for_accuracy || 0

          return {
            ...step,
            type: 'choice',
            options: selectedStyles.map((style: any) => {
              // styleId теперь number, но value в ChoiceOption должен быть string для обратной совместимости
              const styleId = style.styleId || style.style_id
              // Применяем маппинг изображения из бэкенда на локальные файлы
              const mappedImage = mapStyleImageFromBackend({
                code: style.code,
                image: style.image
              })
              return {
                label: style.name || '',
                value: typeof styleId === 'number' ? styleId.toString() : (styleId || ''),
                percentage: style.progressPercent || style.progress_percent || 0,
                photosCount: swipesForAccuracy,
                image: mappedImage
              }
            }),
            multiSelect: true
          }
        }
        return step
      })
    }

    return config
  } catch (error) {
    // No mock fallback: a missing config must surface, not silently render a
    // fabricated survey that scores against nothing.
    console.error('Failed to fetch taste config:', error)
    throw error
  }
}

/**
 * @deprecated Параметр resume больше не поддерживается бекендом
 * Используйте fetchTasteConfig с configId
 */
export async function fetchTasteConfigWithResume(configId: string): Promise<TasteConfig> {
  return fetchTasteConfig({ configId })
}

interface TasteSubmitResponse {
  success: boolean
  message: string
  stylePreferences: TasteResults
}

export async function submitTasteAnswers(data: TasteSubmitRequest): Promise<TasteResults> {
  try {
    const response = await apiClient.post<TasteSubmitResponse>('/taste/submit', data)
    // Бекенд возвращает объект с stylePreferences, извлекаем его
    return response.data.stylePreferences
  } catch (error) {
    console.error('Error submitting taste answers:', error)
    throw error
  }
}

export interface FetchTasteStylePreferencesParams {
  configId?: string
}

/**
 * Получение стилевых предпочтений пользователя
 * Заменяет старый эндпоинт GET /taste/results
 */
export async function fetchTasteStylePreferences(
  params?: FetchTasteStylePreferencesParams
): Promise<TasteResults> {
  try {
    const queryParams = new URLSearchParams()
    if (params?.configId) {
      queryParams.append('config_id', params.configId)
    }

    const url = queryParams.toString()
      ? `/taste/style-preferences?${queryParams.toString()}`
      : '/taste/style-preferences'

    const response = await apiClient.get<TasteResults>(url)
    return response.data
  } catch (error) {
    console.error('Error fetching taste style preferences:', error)
    throw error
  }
}

/**
 * @deprecated Используйте fetchTasteStylePreferences вместо этого
 */
export interface FetchTasteResultsParams {
  tasteConfigId: string
}

/**
 * @deprecated Используйте fetchTasteStylePreferences вместо этого
 */
export async function fetchTasteResults(params: FetchTasteResultsParams): Promise<TasteResults> {
  return fetchTasteStylePreferences({ configId: params.tasteConfigId })
}

export interface FetchTasteProgressParams {
  userProfileId?: number
}

interface TasteProgressResponse {
  overallCompletion: number
  styles: Record<string, {
    styleTagId: number
    styleName: string
    completedSwipes: number
    requiredSwipes: number
    completionPercentage: number
    status: string
  }>
  lastUpdated: string
}

/**
 * Получение прогресса прохождения опроса вкусов
 */
export async function fetchTasteProgress(
  params?: FetchTasteProgressParams
): Promise<TasteProgressResponse> {
  try {
    const queryParams = new URLSearchParams()
    if (params?.userProfileId !== undefined) {
      queryParams.append('user_profile_id', params.userProfileId.toString())
    }

    const url = queryParams.toString()
      ? `/taste/progress?${queryParams.toString()}`
      : '/taste/progress'

    const response = await apiClient.get<TasteProgressResponse>(url)
    return response.data
  } catch (error) {
    console.error('Error fetching taste progress:', error)
    throw error
  }
}

